import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Bell,
  Clock,
  ExternalLink,
  Globe,
  Radio,
  Send,
  Smartphone,
  Store,
  Trash2,
  User,
  Users,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { formatDateTime, formatNumber } from "@/lib/utils";
import { PageHeader } from "@/components/data/toolbar";
import { Button } from "@/components/ui/button";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Skeleton,
  Textarea,
} from "@/components/ui/primitives";
import { ConfirmDialog } from "@/components/data/ConfirmDialog";

interface NotificationHistoryItem {
  id: string;
  title: string;
  body: string;
  target: "all" | "buyers" | "breeders" | "individual";
  target_user: { id: string; name: string | null; phone: string | null } | null;
  image_url: string | null;
  action_url: string | null;
  sent_by_name: string;
  total_recipients: number;
  successful_sends: number;
  created_at: string;
}

export const NotificationsPage = () => {
  const queryClient = useQueryClient();

  const [target, setTarget] = React.useState<"all" | "buyers" | "breeders" | "individual">("all");
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [actionUrl, setActionUrl] = React.useState("");
  const [targetUserId, setTargetUserId] = React.useState("");
  const [deleteId, setDeleteId] = React.useState<string | null>(null);

  const { data: historyData, isLoading } = useQuery({
    queryKey: ["notifications", "history"],
    queryFn: () => api.get<{ items: NotificationHistoryItem[]; total: number }>("/notifications/history"),
  });

  const sendBroadcastMutation = useMutation({
    mutationFn: (payload: {
      title: string;
      body: string;
      target: string;
      target_user_id?: string;
      action_url?: string;
    }) => api.postWithMessage("/notifications/broadcast", payload),
    onSuccess: ({ message }) => {
      toast.success(message);
      setTitle("");
      setBody("");
      setActionUrl("");
      setTargetUserId("");
      void queryClient.invalidateQueries({ queryKey: ["notifications", "history"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Failed to send notification"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteWithMessage(`/notifications/${id}`),
    onSuccess: ({ message }) => {
      toast.success(message);
      setDeleteId(null);
      void queryClient.invalidateQueries({ queryKey: ["notifications", "history"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Failed to delete notification"),
  });

  const history = historyData?.items ?? [];

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error("Please provide both title and body text");
      return;
    }
    sendBroadcastMutation.mutate({
      title: title.trim(),
      body: body.trim(),
      target,
      target_user_id: target === "individual" ? targetUserId.trim() : undefined,
      action_url: actionUrl.trim() || undefined,
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Broadcast &amp; Push Notifications"
        description="Send real-time alerts, announcements, and push notifications to all users, buyers only, or breeders only."
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left Column: Notification Composer */}
        <div className="lg:col-span-7">
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Send className="h-4 w-4 text-primary" /> Create Notification Broadcast
              </CardTitle>
              <CardDescription>
                Compose message and select the audience you want to reach.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSend} className="space-y-4">
                {/* Target Audience Selector */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1.5">
                    Target Audience *
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <button
                      type="button"
                      onClick={() => setTarget("all")}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition-all ${
                        target === "all"
                          ? "border-primary bg-primary/10 text-primary shadow-sm"
                          : "border-border bg-card text-muted-foreground hover:border-border/80"
                      }`}
                    >
                      <Globe className="h-5 w-5 mb-1" />
                      All Users
                    </button>

                    <button
                      type="button"
                      onClick={() => setTarget("buyers")}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition-all ${
                        target === "buyers"
                          ? "border-primary bg-primary/10 text-primary shadow-sm"
                          : "border-border bg-card text-muted-foreground hover:border-border/80"
                      }`}
                    >
                      <Users className="h-5 w-5 mb-1" />
                      Buyers Only
                    </button>

                    <button
                      type="button"
                      onClick={() => setTarget("breeders")}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition-all ${
                        target === "breeders"
                          ? "border-primary bg-primary/10 text-primary shadow-sm"
                          : "border-border bg-card text-muted-foreground hover:border-border/80"
                      }`}
                    >
                      <Store className="h-5 w-5 mb-1" />
                      Breeders Only
                    </button>

                    <button
                      type="button"
                      onClick={() => setTarget("individual")}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition-all ${
                        target === "individual"
                          ? "border-primary bg-primary/10 text-primary shadow-sm"
                          : "border-border bg-card text-muted-foreground hover:border-border/80"
                      }`}
                    >
                      <User className="h-5 w-5 mb-1" />
                      Single User
                    </button>
                  </div>
                </div>

                {target === "individual" && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">
                      Recipient User ID *
                    </label>
                    <Input
                      required
                      placeholder="Paste User UUID"
                      value={targetUserId}
                      onChange={(e) => setTargetUserId(e.target.value)}
                    />
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    Notification Title *
                  </label>
                  <Input
                    required
                    maxLength={100}
                    placeholder="e.g. 🐾 New Verified Breeders Added in Mumbai!"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                {/* Body Message */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    Message Body *
                  </label>
                  <Textarea
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Write announcement or notification details here..."
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                  />
                  <span className="text-[11px] text-muted-foreground block text-right mt-1">
                    {body.length} / 500 characters
                  </span>
                </div>

                {/* Action URL */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    Action URL / Deep Link (Optional)
                  </label>
                  <Input
                    placeholder="e.g. https://getpet.in/listings or /breeders"
                    value={actionUrl}
                    onChange={(e) => setActionUrl(e.target.value)}
                  />
                </div>

                <Button
                  type="submit"
                  loading={sendBroadcastMutation.isPending}
                  className="w-full gap-2 font-medium"
                >
                  <Send className="h-4 w-4" /> Send Notification Broadcast Now
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Live Mobile Push Preview */}
        <div className="lg:col-span-5">
          <Card className="border-border bg-surface/30">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Smartphone className="h-4 w-4 text-muted-foreground" /> Mobile Device Notification Preview
              </CardTitle>
              <CardDescription>
                Simulated appearance on user mobile lockscreen.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mx-auto max-w-sm rounded-2xl border border-border bg-card p-4 shadow-md">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1.5">
                    <span className="grid h-6 w-6 place-items-center rounded-lg bg-primary text-primary-foreground text-xs font-bold">
                      GP
                    </span>
                    <span className="text-xs font-bold text-foreground">GetPet</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground">Now</span>
                </div>

                <p className="text-sm font-bold text-foreground">
                  {title || "Notification Title Appears Here"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  {body || "Your message body content will be shown here to your users when received on their device."}
                </p>

                {actionUrl && (
                  <div className="mt-2.5 flex items-center gap-1 text-[10px] font-medium text-primary">
                    <ExternalLink className="h-3 w-3" /> Tap to open link
                  </div>
                )}
              </div>

              <div className="mt-4 rounded-xl bg-surface/60 p-3 text-xs text-muted-foreground space-y-1">
                <p className="font-semibold text-foreground flex items-center gap-1.5">
                  <Radio className="h-3.5 w-3.5 text-success" /> Multi-Channel Broadcast
                </p>
                <p>
                  Target: <strong className="text-foreground capitalize">{target}</strong>. Delivers to all active web &amp; mobile app subscribers in real time.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Broadcast History */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Broadcast History</CardTitle>
          <CardDescription>
            Log of previously sent notifications and recipient reach.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-xl" />
              ))}
            </div>
          ) : history.length === 0 ? (
            <EmptyState
              icon={Bell}
              title="No broadcast notifications sent yet"
              description="Use the composer above to broadcast announcements to your buyers and breeders."
            />
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-foreground">{item.title}</p>
                      <Badge variant="secondary" className="capitalize text-[10px]">
                        Target: {item.target}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        &middot; {formatNumber(item.total_recipients)} recipient(s)
                      </span>
                    </div>

                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {item.body}
                    </p>

                    <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground/80">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {formatDateTime(item.created_at)}
                      </span>
                      <span>Sent by: {item.sent_by_name}</span>
                      {item.action_url && (
                        <span className="truncate max-w-xs text-primary">{item.action_url}</span>
                      )}
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteId(item.id)}
                    className="self-end text-xs text-destructive hover:text-destructive sm:self-center"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={Boolean(deleteId)}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title="Remove Notification Record?"
        description="This will delete this notification record from the admin history."
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
      />
    </div>
  );
};
